// src/components/quiz/QuestionOrder.jsx
import React from 'react';
import { ArrowRight, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import TranslatableText from '../TranslatableText';

export default function QuestionOrder({ 
  question, 
  userSentence, 
  sentenceWords, 
  feedback, 
  onWordClick, 
   onClearAll,
  onCheck 
}) {
  // Calcular total de palabras necesarias (sin distractores)
  const totalWordsNeeded = question.a 
    ? question.a.split(' ').filter(w => w.trim()).length 
    : userSentence.length;

  // Función para limpiar la construcción
const handleClear = () => {
    if (onClearAll) {
      onClearAll(); // ✅ Usar la función del padre
    } else {
      // Fallback al método anterior (por si acaso)
      for (let i = userSentence.length - 1; i >= 0; i--) {
        onWordClick(userSentence[i], i, 'sentence');
      }
    }
  };
  return (
    <div className="space-y-4 animate-in fade-in duration-500">
      {/* TARJETA DE PREGUNTA CON PROGRESO */}
      <div className="bg-white p-6 rounded-[2.5rem] border-2 border-slate-100 shadow-sm text-center">
        <span className="text-[10px] font-black uppercase tracking-widest text-indigo-400 block mb-2">
          ORDENA LAS PALABRAS
        </span>
        <h3 className="text-xl font-bold text-slate-700 leading-snug mb-3">
           <TranslatableText text={question.q.replace('Traduce:', '').trim()} />
        </h3>

        {/* ✅ CONTADOR DE PROGRESO */}
        <div className="flex items-center justify-center gap-2 mt-3">
          {[...Array(totalWordsNeeded)].map((_, i) => (
            <div
              key={i}
              className={`w-8 h-1.5 rounded-full transition-all duration-300 ${
                i < userSentence.length 
                  ? 'bg-indigo-500 scale-110' 
                  : 'bg-slate-200'
              }`}
            />
          ))}
        </div>
        <p className="text-xs font-bold text-slate-500 mt-2">
          {userSentence.length} de {totalWordsNeeded} palabras
        </p>
      </div>

      {/* ZONA DE CONSTRUCCIÓN CON NÚMEROS */}
      <div className={`min-h-[110px] rounded-3xl border-2 border-dashed transition-all duration-300 flex flex-wrap gap-2 items-center justify-center p-5 relative ${
        feedback === 'correct' 
          ? 'border-green-400 bg-green-50 shadow-lg shadow-green-100' 
          : feedback === 'wrong' 
            ? 'border-red-400 bg-red-50 shadow-lg shadow-red-100 animate-shake' 
            : 'border-slate-300 bg-slate-50/50'
      }`}>
        
        {/* ✅ ICONO DE FEEDBACK */}
        {feedback === 'correct' && (
          <CheckCircle2 
            className="absolute top-2 right-2 text-green-500 animate-in zoom-in" 
            size={24} 
          />
        )}
        {feedback === 'wrong' && (
          <XCircle 
            className="absolute top-2 right-2 text-red-500 animate-in zoom-in" 
            size={24} 
          />
        )}

        {userSentence.length === 0 ? (
          <div className="text-center">
            <ArrowRight className="mx-auto mb-2 text-slate-300 animate-bounce" size={28} />
            <span className="text-slate-400 text-sm font-medium italic">
              Selecciona palabras en orden...
            </span>
          </div>
        ) : (
          userSentence.map((word, idx) => (
            <button
              key={`user-${idx}`}
              onClick={() => !feedback && onWordClick(word, idx, 'sentence')}
              disabled={!!feedback}
              className={`relative group px-5 py-2.5 rounded-2xl font-bold shadow-md border-b-4 transition-all animate-in zoom-in ${
                feedback === 'correct'
                  ? 'bg-green-500 text-white border-green-700 scale-105'
                  : feedback === 'wrong'
                    ? 'bg-red-500 text-white border-red-700'
                    : 'bg-indigo-600 text-white border-indigo-800 hover:bg-indigo-500 active:translate-y-1 active:border-b-0'
              }`}
              style={{ animationDelay: `${idx * 50}ms` }}
            >
              {/* ✅ NÚMERO DE ORDEN */}
              <span className="absolute -top-2 -left-2 w-5 h-5 bg-white text-indigo-600 rounded-full text-xs font-black flex items-center justify-center shadow-md border border-indigo-200">
                {idx + 1}
              </span>
              
              {word}

              {/* ✅ HOVER TOOLTIP */}
              {!feedback && (
                <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  Click para quitar
                </span>
              )}
            </button>
          ))
        )}
      </div>

      {/* BOTÓN DE LIMPIAR */}
      {!feedback && userSentence.length > 0 && (
        <div className="flex justify-end">
          <button
            onClick={handleClear}
            className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 active:scale-95 transition-all"
          >
            <RefreshCw size={14} />
            Limpiar todo
          </button>
        </div>
      )}

      {/* POOL DE PALABRAS CON ANIMACIÓN */}
      <div className="relative">
        {sentenceWords.length === 0 && !feedback && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-green-500 text-white px-4 py-2 rounded-xl font-bold text-sm animate-in zoom-in fade-in">
              ¡Todas las palabras usadas! 🎉
            </div>
          </div>
        )}
        
        <div className={`flex flex-wrap gap-3 justify-center pt-2 transition-opacity duration-300 ${
          sentenceWords.length === 0 && !feedback ? 'opacity-20' : ''
        }`}>
          {sentenceWords.map((word, idx) => (
            <button
              key={`pool-${idx}`}
              onClick={() => !feedback && onWordClick(word, idx, 'pool')}
              disabled={!!feedback}
              className={`px-5 py-3 rounded-2xl font-bold border-b-4 transition-all shadow-sm ${
                feedback 
                  ? 'bg-slate-50 text-slate-300 border-slate-100 opacity-50 cursor-not-allowed'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-indigo-400 hover:text-indigo-600 hover:scale-105 hover:-translate-y-1 active:scale-95 active:translate-y-0'
              }`}
            >
              {word} 
            </button>
          ))}
        </div>
      </div>

      {/* MENSAJE CONTEXTUAL */}
      {!feedback && userSentence.length > 0 && userSentence.length < totalWordsNeeded && (
        <div className="text-center animate-in fade-in slide-in-from-bottom-2 duration-300">
          <p className="text-xs font-bold text-indigo-600 bg-indigo-50 py-2 px-4 rounded-xl inline-block">
            Faltan {totalWordsNeeded - userSentence.length} palabra{totalWordsNeeded - userSentence.length !== 1 ? 's' : ''} más 💡
          </p>
        </div>
      )}

      {/* BOTÓN DE VERIFICACIÓN MEJORADO */}
      {!feedback && userSentence.length > 0 && (
        <div className="pt-4">
          <button
            onClick={onCheck}
            disabled={userSentence.length < totalWordsNeeded}
            className={`w-full font-black py-4 rounded-[2rem] shadow-xl border-b-8 transition-all text-lg flex items-center justify-center gap-3 ${
              userSentence.length >= totalWordsNeeded
                ? 'bg-indigo-600 text-white border-indigo-900 shadow-indigo-100 hover:bg-indigo-500 active:border-b-0 active:translate-y-2'
                : 'bg-slate-300 text-slate-500 border-slate-400 cursor-not-allowed opacity-50'
            }`}
          >
            COMPROBAR
            {userSentence.length >= totalWordsNeeded && (
              <CheckCircle2 size={20} />
            )}
          </button>
        </div>
      )}

      {/* ESTILOS ADICIONALES */}
      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
        
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
}
